import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import AlertGenericModal from 'react/containers/modals/AlertGenericModal';
import { BaseCrud } from 'react/containers/layout/Crud';

import { actionCreators } from 'react/api/actions/integrations';
import { getResourceTo } from 'react/utils/utils';
import { getIntegration, getIntegrationLogs } from 'react/api/selectors/integrations';

import IntegrationFormSection from 'react/components/Integrations/IntegrationFormSection';
import { useIntegrations } from 'react/hooks/integrations.hook';

function IntegrationLogCrud(props) {
  const nameSpace = useMemo(() => `IntegrationLog_${props.id}`, [props.id]);

  const dispatch = useDispatch();
  const {
    getActiveFilters,
    downloadCSVFromData,
    setSortBy,
    setCustomColumnsFormatter,
    setCustomOnClickRow
  } = useIntegrations(nameSpace);

  const integrationLogs = useSelector((state) => getIntegrationLogs(state, props.id));
  const { integration } = useSelector((state) => getIntegration(state, props.id)?.toJS() || {});

  const isModalConfirmRetryOpen = useSelector(({ integrations }) => integrations.get('isModalConfirmRetryOpen'));
  const { isRetrying, error } = useSelector(({ integrations }) => integrations.get('retryLogsState').toJS());

  const errorMessage = useMemo(
    () => (!isRetrying && error ? error.message : null),
    [error, isRetrying]
  );

  const getParsedCollectionData = (data) => {
    const arrayOfData = data.map((item) => new App.Models.IntegrationLog(item));
    const parsedData = new App.Collections.IntegrationLogs();

    arrayOfData.forEach((item) => {
      parsedData.add(item);
    });

    return parsedData;
  };

  const getActualPage = () => {
    const paginationFirst = App.Forms[nameSpace].pagination.first;

    return paginationFirst ? paginationFirst / 51 : 0;
  };

  const addDataToGrid = (data) => {
    const columns = App.Forms[nameSpace].table.getColumns();
    const parsedData = getParsedCollectionData(data);

    if (!data.length) {
      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, true);
      Monet.Grid.Utils.showGridNoResult(nameSpace);

      return;
    }

    Monet.Grid.Utils.hideGridNoResult(nameSpace);

    const integrationLogData = App.Forms[nameSpace].dataProvider.data;
    const page = getActualPage();

    if (integrationLogData.length === 0 || page === 0) {
      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, true);
    } else {
      Monet.Grid.appendDataToGridAfterPagination(nameSpace, columns, parsedData);
    }
  };

  const setCustomExecuteFilter = () => {
    App.Forms[nameSpace].filterBuilder.customExecuteFilter = () => {
      Monet.Grid.Utils.hideGridNoResult(nameSpace);

      const page = getActualPage();

      const { id } = props;

      const activeFilters = getActiveFilters();

      const filtersPayload = {
        configuration: id,
        page: Math.ceil(page),
        size: 51
      };

      Object.assign(filtersPayload, activeFilters);

      dispatch(actionCreators.fetchIntegrationLogs({ filtersPayload }));
    };
  };

  const setCustomSaveList = () => {
    const {
      actions: { exportFormats },
      fields: formFields
    } = App.Forms[nameSpace];

    App.Forms[nameSpace].actions.exportFormats.saveFieldList = () => {
      exportFormats.handlerToggleLoadingInBtn();

      const fields = exportFormats.getFieldsSelecteds();

      const date = new Date();
      const fileName = `IntegrationLogs_${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.csv`;

      downloadCSVFromData({
        formFields,
        fields,
        fileName,
        exportFormats
      });
    };
  };

  const setCustomFilterBuilderFields = () => {
    let fieldsList = App.Forms[nameSpace]?.filterBuilder?.fieldList;

    delete fieldsList.httpStatus_Range;
    delete fieldsList.message;
    delete fieldsList.requestDate;
    delete fieldsList.requestBody;

    fieldsList = Object.keys(fieldsList);

    App.Forms[nameSpace]?.filterBuilder?.setFieldsToDisplayOnFilterBuilder(fieldsList);
    App.Forms[nameSpace]?.filterBuilder?.render();
  };

  const setCustomCustomizeColumns = () => {
    App.Forms[nameSpace].actions.customizecolumns.setCallBacks({
      afterSaveList: () => {
        const router = App.Objects[nameSpace];

        router.list();
      }
    });
  };

  const removeSaveGridLayoutButton = () => {
    const saveGridButton = document.querySelector(`#IntegrationLog_${props.id} .saveLayout.gridActionDiv`);

    saveGridButton && saveGridButton.remove();
  };

  const setDataProvider = () => {
    const dataProvider = App.Forms[nameSpace]?.dataProvider?.data || [];
    const integrationLogsInState = GreenmileReduxStore
      .getState()
      .integrations.getIn(['integrationsLogsPage', props.id, 'logs'], []);

    if (!dataProvider?.length && integrationLogsInState?.size) {
      const parsedData = getParsedCollectionData(integrationLogsInState.toJS());
      const columns = App.Forms[nameSpace].table.getColumns();

      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, true);
    }
  };

  const confirmRetryLogCallback = () => {
    dispatch(actionCreators.retryIntegrationLogs());
  };

  const cancelRetryLogCallback = () => {
    dispatch(actionCreators.setRetryLogsState({
      logsToRetry: [],
      isRetrying: false,
      error: null
    }));

    dispatch(actionCreators.toggleRetryIntegrationLogsConfirmModal());
  };

  useEffect(() => {
    App.Objects[nameSpace].afterPopulateGrid = () => {
      setCustomExecuteFilter();
      setCustomSaveList();
      setCustomCustomizeColumns();
      removeSaveGridLayoutButton();
      setDataProvider();
      setSortBy();
      setCustomFilterBuilderFields();
      setCustomColumnsFormatter();
      setCustomOnClickRow();

      App.Forms[nameSpace].filterBuilder.setAllowSavePreferences(false);
    };
  }, []);

  useEffect(() => {
    const { id } = props;
    const activeFilters = getActiveFilters();

    const filtersPayload = {
      configuration: id
    };

    Object.assign(filtersPayload, activeFilters);

    dispatch(actionCreators.fetchIntegrationLogs({ filtersPayload }));
  }, []);

  useEffect(() => {
    const { updateTitle } = props;

    updateTitle({
      key: nameSpace,
      title: getResourceTo('integrationLog.section.general.title', integration?.name || ' ')
    });
  }, [integration]);

  useEffect(() => {
    if (integrationLogs?.size >= 0) {
      const data = integrationLogs.toJS();

      addDataToGrid(data);
    }
  }, [integrationLogs]);

  return (
    <>
      <BaseCrud
        entity={`IntegrationLog_${props.id}`}
        updateMethod="PATCH"
        tabName={getResourceTo('IntegrationLog.screen.title', integration?.name || 'Integration')}
        customOptions={{
          columnCheckBox: {
            show: true,
            checkBoxEventClass: [Monet.Grid.ActionsModules.IntegrationLogRetry],
            options: {
              checkOnClickRow: true,
              isDisabled: (row) => {
                const item = App.Forms[nameSpace].dataProvider.getItem(row);
                const { attributes } = item.model;

                return item && Object.keys(attributes).length && attributes.httpStatus === 200;
              }
            }
          },
          rowHeight: 32,
          sanitizer: null
        }}
        customActions={[
          {
            name: 'btn_delete'
          }
        ]}
        {...props}
      >
        <IntegrationFormSection title='integrationLog.section.general.title' />
      </BaseCrud>

      <AlertGenericModal
        open={isModalConfirmRetryOpen}
        i18n={Monet.I18n}
        isLoading={isRetrying}
        title="RetryIntegrations.Modal.Title"
        message="RetryIntegrations.Modal.Message"
        confirmClickButton={confirmRetryLogCallback}
        cancelClickButton={cancelRetryLogCallback}
        errorMessage={errorMessage}
        customConfirmButtonResource="RetryIntegrations.Modal.Title"
      />
    </>
  );
}

IntegrationLogCrud.propTypes = {
  id: PropTypes.string.isRequired,
  updateTitle: PropTypes.func.isRequired
};

export default IntegrationLogCrud;
